import React, { useEffect, useMemo, useState } from "react";
import { Ic, Modal, inputStyle, btnPrimary, btnSecondary, btnGhost, showStyledAlert, showStyledConfirm } from "../components/UI";
import { addDevelopmentNote, addDevelopmentNoteReply, deleteDevelopmentNote, getDevelopmentNotes, updateDevelopmentNote } from "../utils/firestore";

const STATUS_META = {
  open: { label: "פתוח", bg: "#FEF2F2", color: "#B91C1C", border: "#FECACA" },
  in_progress: { label: "בטיפול", bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
  done: { label: "טופל", bg: "#ECFDF5", color: "#15803D", border: "#BBF7D0" },
};

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

function formatDateTime(value) {
  if (!value) return "—";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.border}`,
    }}>
      {meta.label}
    </span>
  );
}

function formatFileSize(size) {
  const value = Number(size || 0);
  if (!value) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentPreview({ attachment, label = "תמונה מצורפת" }) {
  if (!attachment?.url) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>{label}</div>
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        style={{ display: "inline-flex", flexDirection: "column", gap: 8, textDecoration: "none" }}
      >
        <img
          src={attachment.url}
          alt={attachment.name || label}
          style={{ width: "100%", maxWidth: 280, maxHeight: 220, objectFit: "cover", borderRadius: 14, border: "1px solid #E2E8F0", background: "#F8FAFC" }}
        />
        <span style={{ fontSize: 12, color: "#2563EB", fontWeight: 600 }}>
          {attachment.name || "פתח תמונה"}
          {attachment.size ? ` · ${formatFileSize(attachment.size)}` : ""}
        </span>
      </a>
    </div>
  );
}

export default function DevelopmentNotesPage({ currentUser, showToast, isMobile = false }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const loadNotes = async () => {
    try {
      setLoading(true);
      const rows = await getDevelopmentNotes();
      setNotes(rows);
    } catch (e) {
      await showStyledAlert(`שגיאה בטעינת הערות פיתוח: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return notes.filter((item) => {
      if (filterStatus !== "all" && (item.status || "open") !== filterStatus) return false;
      if (!q) return true;
      const repliesText = Array.isArray(item.replies)
        ? item.replies.map((reply) => `${reply.authorName || ""} ${reply.body || ""}`).join(" ")
        : "";
      return [item.title || "", item.body || "", item.authorName || "", repliesText].join(" ").toLowerCase().includes(q);
    });
  }, [notes, search, filterStatus]);

  const save = async (form) => {
    const payload = {
      title: (form.title || "").trim(),
      body: (form.body || "").trim(),
      status: form.status || "open",
      authorId: currentUser?.id || "",
      authorName: currentUser?.name || currentUser?.email || "משתמש",
      authorRole: currentUser?.role || "",
    };
    if (modal?.type !== "edit" || form.attachmentChanged) {
      payload.attachment = form.attachment || null;
      payload.attachmentFile = form.attachmentFile || null;
    }
    if (!payload.title || !payload.body) {
      await showStyledAlert("יש למלא כותרת ותיאור.");
      return;
    }
    try {
      if (modal?.type === "edit") {
        await updateDevelopmentNote(modal.item.id, {
          ...payload,
          previousAttachment: modal.item?.attachment || null,
        });
        showToast("הערת הפיתוח עודכנה");
      } else {
        await addDevelopmentNote(payload);
        showToast("הערת הפיתוח נוספה");
      }
      setModal(null);
      await loadNotes();
    } catch (e) {
      await showStyledAlert(`שגיאה בשמירת הערת פיתוח: ${e.message}`);
    }
  };

  const saveReply = async (form) => {
    const body = (form.body || "").trim();
    if (!body) {
      await showStyledAlert("יש למלא תגובה.");
      return;
    }
    try {
      await addDevelopmentNoteReply(modal.item.id, {
        body,
        authorId: currentUser?.id || "",
        authorName: currentUser?.name || currentUser?.email || "משתמש",
        authorRole: currentUser?.role || "",
        attachment: form.attachment || null,
        attachmentFile: form.attachmentFile || null,
      });
      showToast("התגובה נוספה");
      setModal(null);
      await loadNotes();
    } catch (e) {
      await showStyledAlert(`שגיאה בשמירת תגובה: ${e.message}`);
    }
  };

  const remove = async (item) => {
    if (!await showStyledConfirm(`למחוק את ההערה "${item.title}"?`)) return;
    try {
      await deleteDevelopmentNote(item);
      showToast("הערת הפיתוח נמחקה");
      await loadNotes();
    } catch (e) {
      await showStyledAlert(`שגיאה במחיקת הערת פיתוח: ${e.message}`);
    }
  };

  return (
    <div style={{ direction: "rtl" }}>
      <div style={{ background: "white", borderRadius: 16, border: "1px solid #E8E8ED", padding: "16px 20px", marginBottom: 14, boxShadow: "0 1px 4px rgba(0,0,0,.03)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "Assistant", color: "#1A1A2E" }}>הערות לפיתוח</div>
            <div style={{ fontSize: 13, color: "#8E8EA0" }}>מקום מרכזי לרכזים ולמשתמשים להשאיר בקשות, תקלות ושיפורים.</div>
          </div>
          <button onClick={() => setModal({ type: "add" })} style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px" }}>
            <Ic.Plus /> הערה חדשה
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
          {[
            { label: "סה\"כ הערות", value: notes.length, color: "#2563EB" },
            { label: "פתוחות", value: notes.filter((item) => (item.status || "open") === "open").length, color: "#DC2626" },
            { label: "בטיפול", value: notes.filter((item) => item.status === "in_progress").length, color: "#D97706" },
            { label: "טופלו", value: notes.filter((item) => item.status === "done").length, color: "#16A34A" },
          ].map((card) => (
            <div key={card.label} style={{ background: "#FCFCFE", border: "1px solid #ECECF2", borderRadius: 14, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "#8E8EA0", marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: card.color, fontFamily: "Assistant" }}>{card.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש הערה..."
            style={{ ...inputStyle, borderRadius: 12, minWidth: isMobile ? "100%" : 280 }}
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ ...inputStyle, borderRadius: 12, width: isMobile ? "100%" : 180, cursor: "pointer" }}>
            <option value="all">כל הסטטוסים</option>
            <option value="open">פתוח</option>
            <option value="in_progress">בטיפול</option>
            <option value="done">טופל</option>
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {loading && <div style={{ textAlign: "center", padding: 32, color: "#A3A3B5" }}>טוען...</div>}
        {!loading && filtered.map((item) => {
          const isDone = (item.status || "open") === "done";
          return (
          <div key={item.id} style={{ background: isDone ? "linear-gradient(180deg, #F3FFF6, #ECFDF3)" : "white", borderRadius: 16, border: `1px solid ${isDone ? "#BBF7D0" : "#E8E8ED"}`, padding: "16px 18px", boxShadow: isDone ? "0 6px 18px rgba(22,163,74,.08)" : "0 1px 4px rgba(0,0,0,.03)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "Assistant", color: "#171717", marginBottom: 4 }}>{item.title || "ללא כותרת"}</div>
                <div style={{ fontSize: 12, color: "#8E8EA0" }}>
                  {item.authorName || "—"} · {formatDateTime(item.createdAt)}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusPill status={item.status || "open"} />
                <button onClick={() => setModal({ type: "reply", item })} style={{ ...btnSecondary, padding: "7px 10px", fontSize: 12 }}>תגובה</button>
                <button onClick={() => setModal({ type: "edit", item })} style={{ ...btnSecondary, padding: "7px 10px", fontSize: 12 }}>עריכה</button>
                <button onClick={() => remove(item)} style={{ ...btnGhost, color: "#DC2626" }}><Ic.Trash /></button>
              </div>
            </div>
            <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.7, color: "#404040", fontSize: 14 }}>{item.body || "—"}</div>
            <AttachmentPreview attachment={item.attachment} />
            <div style={{ marginTop: 14, borderTop: "1px solid #F1F5F9", paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: "#64748B", marginBottom: 10 }}>
                תגובות {Array.isArray(item.replies) ? `(${item.replies.length})` : "(0)"}
              </div>
              {Array.isArray(item.replies) && item.replies.length > 0 ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {[...(item.replies || [])]
                    .slice()
                    .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime())
                    .map((reply) => (
                      <div key={reply.id || `${reply.authorId}-${reply.createdAt}`} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "10px 12px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{reply.authorName || "משתמש"}</div>
                          <div style={{ fontSize: 11, color: "#94A3B8" }}>{formatDateTime(reply.createdAt)}</div>
                        </div>
                        <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#475569", fontSize: 13 }}>{reply.body || "—"}</div>
                        <AttachmentPreview attachment={reply.attachment} label="תמונה בתגובה" />
                      </div>
                    ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#94A3B8" }}>אין תגובות עדיין</div>
              )}
            </div>
          </div>
        )})}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 40, color: "#A3A3B5", background: "white", borderRadius: 16, border: "1px solid #E8E8ED" }}>
            אין הערות להצגה
          </div>
        )}
      </div>

      {modal?.type !== "reply" && modal && <DevelopmentNoteModal initial={modal.type === "edit" ? modal.item : null} onClose={() => setModal(null)} onSave={save} />}
      {modal?.type === "reply" && <DevelopmentNoteReplyModal item={modal.item} onClose={() => setModal(null)} onSave={saveReply} />}
    </div>
  );
}

function DevelopmentNoteModal({ initial = null, onClose, onSave }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    body: initial?.body || "",
    status: initial?.status || "open",
    attachment: initial?.attachment || null,
    attachmentFile: null,
  });
  const [saving, setSaving] = useState(false);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState("");
  const [attachmentChanged, setAttachmentChanged] = useState(false);

  useEffect(() => {
    if (!form.attachmentFile) {
      setAttachmentPreviewUrl("");
      return undefined;
    }
    const previewUrl = URL.createObjectURL(form.attachmentFile);
    setAttachmentPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [form.attachmentFile]);

  const currentAttachment = form.attachmentFile
    ? {
        url: attachmentPreviewUrl,
        name: form.attachmentFile.name,
        size: form.attachmentFile.size,
      }
    : form.attachment;

  const onPickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      await showStyledAlert("אפשר לצרף רק קובצי תמונה.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      await showStyledAlert("גודל התמונה המקסימלי הוא 5MB.");
      return;
    }
    setForm((prev) => ({ ...prev, attachmentFile: file }));
    setAttachmentChanged(true);
  };

  const submit = async () => {
    setSaving(true);
    await onSave({
      ...form,
      attachmentChanged,
      attachment: form.attachmentFile ? null : form.attachment,
    });
    setSaving(false);
  };

  return (
    <Modal
      title={initial ? "עריכת הערת פיתוח" : "הערת פיתוח חדשה"}
      onClose={onClose}
      footer={
        <>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px" }}>
            {saving ? "שומר..." : "שמירה"}
          </button>
          <button onClick={onClose} style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px" }}>ביטול</button>
        </>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>כותרת</label>
        <input style={{ ...inputStyle, borderRadius: 12 }} value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>תיאור</label>
        <textarea style={{ ...inputStyle, borderRadius: 12, minHeight: 140, resize: "vertical" }} value={form.body} onChange={(e) => setForm((prev) => ({ ...prev, body: e.target.value }))} />
      </div>
      <div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>סטטוס</label>
        <select style={{ ...inputStyle, borderRadius: 12, cursor: "pointer" }} value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
          <option value="open">פתוח</option>
          <option value="in_progress">בטיפול</option>
          <option value="done">טופל</option>
        </select>
      </div>
      <div style={{ marginTop: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>תמונה</label>
        <input type="file" accept="image/*" onChange={onPickFile} />
        <div style={{ fontSize: 12, color: "#8E8EA0", marginTop: 6 }}>ניתן לצרף קובץ תמונה עד 5MB.</div>
        {(form.attachment || form.attachmentFile) && (
          <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
            <AttachmentPreview attachment={currentAttachment} />
            <button
              onClick={() => {
                setForm((prev) => ({ ...prev, attachment: null, attachmentFile: null }));
                setAttachmentChanged(true);
              }}
              type="button"
              style={{ ...btnGhost, color: "#DC2626", marginTop: 8 }}
            >
              הסר תמונה
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DevelopmentNoteReplyModal({ item, onClose, onSave }) {
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState("");

  useEffect(() => {
    if (!attachment) {
      setAttachmentPreviewUrl("");
      return undefined;
    }
    const previewUrl = URL.createObjectURL(attachment);
    setAttachmentPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [attachment]);

  const attachmentPreview = attachment
    ? {
        url: attachmentPreviewUrl,
        name: attachment.name,
        size: attachment.size,
      }
    : null;

  const onPickFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      await showStyledAlert("אפשר לצרף רק קובצי תמונה.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      await showStyledAlert("גודל התמונה המקסימלי הוא 5MB.");
      return;
    }
    setAttachment(file);
  };

  const submit = async () => {
    setSaving(true);
    await onSave({ body, attachmentFile: attachment, attachment: null });
    setSaving(false);
  };

  return (
    <Modal
      title="תגובה להערת פיתוח"
      onClose={onClose}
      footer={
        <>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, borderRadius: 12, padding: "9px 18px" }}>
            {saving ? "שומר..." : "הוסף תגובה"}
          </button>
          <button onClick={onClose} style={{ ...btnSecondary, borderRadius: 12, padding: "9px 16px" }}>ביטול</button>
        </>
      }
    >
      <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>מגיב על</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A", fontFamily: "Assistant" }}>{item?.title || "ללא כותרת"}</div>
      </div>
      <div>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>תגובה</label>
        <textarea style={{ ...inputStyle, borderRadius: 12, minHeight: 140, resize: "vertical" }} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      <div style={{ marginTop: 16 }}>
        <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525264", marginBottom: 6 }}>תמונה</label>
        <input type="file" accept="image/*" onChange={onPickFile} />
        {attachmentPreview && (
          <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
            <AttachmentPreview attachment={attachmentPreview} label="תמונה לתגובה" />
            <button onClick={() => setAttachment(null)} type="button" style={{ ...btnGhost, color: "#DC2626", marginTop: 8 }}>
              הסר תמונה
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
